import React, { useMemo } from 'react';
import { TimelineItemType } from 'types/TimelineTypes';
import TimelineUpdate from './TimelineUpdate';
import TimelineNote from './TimelineNote';
import TimelineTask from './TimelineTask';
import { UserType } from '../../views/users/UserTypes';
import { TaskType } from '../../views/tasks/TaskTypes';
import { useTranslation } from 'react-i18next';
import TimelinePayment from './TimelinePayment';
import TimelineEmail from './TimelineEmail';
import TimelineEmailReply from './TimelineEmailReply';
import TimelineIntegrationNotification from './TimelineIntegrationNotification';
import TimelinePortalEvent from './TimelinePortalEvent';
import TimelineSms from './TimelineSms';

/*
 * GenerateTimelineComponents.tsx
 * Function that is responsible for generate the right timeline components. First it maps the fetched users
 * and tasktypes. This is needed to make filtering faster. Then it generates the timeline components based
 * of the object type of the timeline item: if it's an update, note, task or email. In some timelines it is
 * desirable to only show updates. Therefore, if the showOnlyTimelineItems option is set to true, all other
 * timeline items except updates will be filtered out. Since we want to display in the timeline items which 
 * person added the item, and this data is not available in the timeline item object itself, we pass this 
 * along from the usermap function. The same applies to tasks, with tasks we want to show the correct icon so 
 * that we pass on the task type. Finally, this function also ensures that any pinned note is rendered.
 */

type TimelineComponentsRenderProps = {
    renderPinnedNote: () => JSX.Element | null;
    timelineComponents: (JSX.Element | null)[];
}

type GenerateTimelineComponentsProps = {
    placement: 'summary' | 'page';
    timelineItems: TimelineItemType[];
    showOnlyTimelineUpdates: boolean | undefined;
    pinnedNote: TimelineItemType | null;
    users: UserType[];
    taskTypes: TaskType[];
    timeline_page: string;
    timeline_page_id: number;
    children: (props: TimelineComponentsRenderProps) => JSX.Element;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

export const GenerateTimelineComponents: React.FC<GenerateTimelineComponentsProps> = ({
    placement, timelineItems, showOnlyTimelineUpdates, pinnedNote, users, 
    taskTypes, timeline_page_id, timeline_page, children, onSubmitSuccess
}) => {
    const { t } = useTranslation();

    // Map the users to query on them while generating the timeline components
    const userMap = useMemo(() => {
        const map = new Map<string | number, UserType>();
        users.forEach(user => map.set(user.user_hash, user));
        return map;
    }, [users]);

    // Map the users by id to query on them while generating the timeline components
    const userMapById = useMemo(() => {
        const map = new Map<number, UserType>();
        users.forEach(user => map.set(user.id, user));
        return map;
    }, [users]);

    // Map the task types to query on them while generating the timeline components
    const taskTypeMap = useMemo(() => {
        const map = new Map<number, TaskType>();
        taskTypes.forEach(taskType => map.set(taskType.id, taskType));
        return map;
    }, [taskTypes]);

    // Split the timeline in open tasks and history items
    const { openItems, historyItems } = useMemo(() => {

        // Reverse the timeline items and filter out the pinned note if it exists
        let filteredItems = [...timelineItems].reverse();

        // Filter out hidden integration notifications
        filteredItems = filteredItems.filter((item) => {

            // Check wheter the item is an integration notification
            const integrationNotification = item.integration_notification;
            if (integrationNotification) {
                // Always show pending/success notifications
                if (['pending', 'success'].includes(integrationNotification.severity)) {
                    return true;
                }

                // Only show unresolved info/warning/error notifications
                if (['info', 'warning', 'error'].includes(integrationNotification.severity) && !integrationNotification.resolved) {
                    return true;
                }

                return false;
            }

            // Keep all non integration notification items
            return true;
        });

        // Filter the pinned note out of the items
        if (pinnedNote) {
            filteredItems = filteredItems.filter(item => item.timeline_object_id !== pinnedNote.timeline_object_id)
        }

        const openItems: TimelineItemType[] = [];
        const historyItems: TimelineItemType[] = [];

        // Divide the items over the open items and history items
        filteredItems.forEach(item => {
            // Still scheduled sms messages belong to the planned section
            const isScheduledSms = (
                item.timeline_object_type === 'sms_message'
                && ['scheduled', 'retrying'].includes(item.sms_message?.status || '')
            );
            if ((item.timeline_object_type === 'task' && item.task?.checked === false) || isScheduledSms) {
                openItems.push(item);
            } else {
                historyItems.push(item);
            }
        });

        return { openItems, historyItems };
    }, [timelineItems, pinnedNote]);

    // Render the timeline items
    const renderItem = (item: TimelineItemType): JSX.Element | null => {

        // Only shows timeline updates in case the showOnlyTimelineUpdates is set to true
        if (showOnlyTimelineUpdates && item.timeline_object_type !== 'update') {
            return null;
        }

        // Renders the timeline components
        switch(item.timeline_object_type) {
            case 'update':
                // Get the user data which created the update
                const updateCreatedBy = item.update?.user_hash ? userMap.get(String(item.update?.user_hash)) : null;

                // Renders the component
                return <TimelineUpdate 
                            key={item.id} 
                            placement={placement}
                            item={item} 
                            createdBy={updateCreatedBy} 
                        />;

            case 'note':
                if (item.note) {
                    // Get the user data which created the note
                    const noteCreatedBy = item.note?.created_by_user_hash ? userMap.get(String(item.note?.created_by_user_hash)) : null;

                    // To hide the pin icon in the note, determine if this note isn't pinned 
                    const isAnotherPinned = pinnedNote && item.timeline_object_id !== pinnedNote.timeline_object_id;

                    // Renders the component
                    return <TimelineNote 
                                placement={placement}
                                key={item.id} 
                                note={item.note}
                                timeline_page={timeline_page} 
                                timeline_page_id={timeline_page_id} 
                                item={item} 
                                createdBy={noteCreatedBy} 
                                isAnotherPinned={isAnotherPinned || undefined} 
                                onSubmitSuccess={onSubmitSuccess}
                            />;
                }

            case 'task':
                if (item.task) {
                    // Get the user data of the task assignee
                    const taskAssignee = item.task?.assignee ? userMapById.get(item.task?.assignee) : null;

                    // Get the task type data based on the task type id
                    const taskType = item.task?.type ? taskTypeMap.get(item.task?.type) : null;

                    // Renders the component
                    return <TimelineTask 
                                placement={placement}
                                key={item.id} 
                                task={item.task}
                                item={item} 
                                timeline_page={timeline_page} 
                                timeline_page_id={timeline_page_id} 
                                assignee={taskAssignee} 
                                tasktype={taskType}
                                onSubmitSuccess={onSubmitSuccess}
                            />
                }

            case 'payment':
                if (item.payment) {
                    // Get the user data of the payment registrar
                    const userObject = item.payment?.registrated_by ? userMapById.get(item.payment?.registrated_by) : null;

                    // Render the component
                    return <TimelinePayment
                                key={item.id}
                                item={item}
                                payment={item.payment}
                                timeline_page_id={timeline_page_id}
                                registratedBy={userObject}
                                onSubmitSuccess={onSubmitSuccess}
                            />
                }

            case 'email':
                if (item.email) {
                    // Get the user data of the sender
                    const userObject = item.email?.sent_by ? userMapById.get(item.email?.sent_by) : null;

                    // Render the component
                    return <TimelineEmail
                                placement={placement}
                                key={item.id}
                                item={item}
                                email={item.email}
                                timeline_page={timeline_page} 
                                timeline_page_id={timeline_page_id} 
                                sentBy={userObject}
                                onSubmitSuccess={onSubmitSuccess}
                            />
                }

            case 'email_reply':
                if (item.email_reply) {
                    // Render the component
                    return <TimelineEmailReply
                                placement={placement}
                                key={item.id}
                                item={item}
                                reply={item.email_reply}
                                timeline_page={timeline_page}
                                timeline_page_id={timeline_page_id}
                            />
                }

            case 'integrationnotification':
                if (item.integration_notification) {
                    return (
                        <TimelineIntegrationNotification
                            key={item.id}
                            placement={placement}
                            item={item}
                        />
                    );
                }

            case 'proposalportalevent':
                if (item.proposal_portal_event) {
                    return (
                        <TimelinePortalEvent
                            key={item.id}
                            placement={placement}
                            item={item}
                        />
                    );
                }

            case 'sms_message':
                if (item.sms_message) {
                    return (
                        <TimelineSms
                            key={item.id}
                            placement={placement}
                            item={item}
                            smsMessage={item.sms_message}
                            onSubmitSuccess={onSubmitSuccess}
                        />
                    );
                }

            default:
                return null;
        }
    };

    // Renders the pinned note to put seperated on top of the timeline
    const renderPinnedNote = () => {
        if (pinnedNote && pinnedNote.note) {
            // Maps the user hash of the note to the user object and pas it to the component
            const noteCreatedBy = pinnedNote.note.created_by_user_hash ? userMap.get(String(pinnedNote.note.created_by_user_hash)) : null;

            // Renders the timeline note component
            return <TimelineNote 
                        placement={placement}
                        key={pinnedNote.id} 
                        item={pinnedNote} 
                        note={pinnedNote.note}
                        timeline_page={timeline_page} 
                        timeline_page_id={timeline_page_id}
                        createdBy={noteCreatedBy} 
                        isPinned={true} 
                        onSubmitSuccess={onSubmitSuccess}
                    />;
        }
        return null;
    };

    // Generate the timeline
    const timelineComponents = useMemo(() => {
        const components: (JSX.Element | null)[] = [];

        // Determine if the timeline has open items
        const hasOpenItems = openItems.length > 0;
    
        if (hasOpenItems) {
            components.push(
                <div key="todo-section" className="timeline-section todo">
                    <h6 className="timeline-section-header">
                        {t('timeline.general.todo_section_label')}
                    </h6>
                    {openItems.map(renderItem)}
                </div>
            );
        }
    
        components.push(
            <div key="history-section" className="timeline-section history">
                {hasOpenItems && (
                    <h6 className="timeline-section-header">
                        {t('timeline.general.history_section_label')}
                    </h6>
                )}
                {historyItems.map(renderItem)}
            </div>
        );
    
        return components;
    }, [openItems, historyItems, showOnlyTimelineUpdates]);

    // Return the components
    return children({
        renderPinnedNote,
        timelineComponents
    });
}